import { useCallback, useMemo, useState } from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";

import {
  IssueByIdDocument,
  ShareItemDocument,
  ShareItemTeamDocument,
  ShareWorkflowDocument,
  ShareWorkflowTeamDocument,
  TeamsWithoutItemAccessDocument,
  UsersWithoutItemAccessDocument,
  type ObjectAccessLevel,
} from "#app/lib/data";
import type {
  MentionAccessTeam,
  MentionAccessUser,
  MentionAccessVariant,
} from "#app/components/issue-view/activity/mention-access-dialog";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { AnalyticsContext, IssueEvent, trackEvent } from "#app/lib/analytics";

const trackGrant = (
  object: "issue" | "workflow",
  accessLevel: ObjectAccessLevel,
  pending: { users: unknown[]; teams: unknown[] },
) =>
  trackEvent({
    context: AnalyticsContext.Issue,
    event: IssueEvent.GrantAccess,
    data: {
      object,
      accessLevel,
      userCount: pending.users.length,
      teamCount: pending.teams.length,
    },
  });

// State + Apollo plumbing for the global mention-access dialog mount.
// Kept in lib/hooks/ per the consumer-app rule that Apollo calls live
// outside component files. Two halves:
//   - `lookupMissing(issueId, userIds)` — server check.
//   - `grantItemAccess(level)` / `grantWorkflowAccess(level)` — write
//     paths that hit shareItem / shareWorkflow for every pending user.
// The dialog component owns presentation; this hook owns data.

export type PendingState = {
  issueId: string;
  users: MentionAccessUser[];
  // Teams lacking access (Teams-property assignments) — granted through
  // the team share mutations alongside the per-user grants.
  teams: MentionAccessTeam[];
  canManageSharing: boolean;
  // Which flow raised the prompt — drives the dialog copy.
  variant: MentionAccessVariant;
} | null;

export function useMentionAccessDialog() {
  const client = useApolloClient();
  const [shareItem] = useMutation(ShareItemDocument);
  const [shareItemTeam] = useMutation(ShareItemTeamDocument);
  const [shareWorkflow] = useMutation(ShareWorkflowDocument);
  const [shareWorkflowTeam] = useMutation(ShareWorkflowTeamDocument);

  const [pending, setPending] = useState<PendingState>(null);
  const [submitting, setSubmitting] = useState(false);

  // Resolve the parent workflow for the active issue so workflow-
  // level grants have a target. Cache-first; the originating screen
  // (kanban / issue-view) usually has IssueById primed already.
  const workflowVars = useMemo(
    () => ({ issueId: pending?.issueId ?? "" }),
    [pending?.issueId],
  );
  const { data: issueData } = useQuery(IssueByIdDocument, {
    variables: workflowVars,
    skip: !pending,
    fetchPolicy: "cache-first",
  });
  const workflowId = issueData?.issue?.workflowId ?? null;

  const lookupMissing = useCallback(
    async (issueId: string, userIds: string[], teamIds: string[] = []) => {
      const [users, teams] = await Promise.all([
        userIds.length
          ? client
              .query({
                query: UsersWithoutItemAccessDocument,
                variables: { issueId, userIds },
                fetchPolicy: "network-only",
              })
              .then(({ data }) => data?.usersWithoutItemAccess ?? [])
          : Promise.resolve([]),
        teamIds.length
          ? client
              .query({
                query: TeamsWithoutItemAccessDocument,
                variables: { issueId, teamIds },
                fetchPolicy: "network-only",
              })
              .then(({ data }) => data?.teamsWithoutItemAccess ?? [])
          : Promise.resolve([]),
      ]);
      return { users, teams };
    },
    [client],
  );

  const close = useCallback(() => {
    setPending(null);
    setSubmitting(false);
  }, []);

  const grantItemAccess = useCallback(
    async (level: ObjectAccessLevel) => {
      if (!pending) return;
      setSubmitting(true);
      try {
        for (const u of pending.users) {
          await shareItem({
            variables: {
              input: {
                issueId: pending.issueId,
                userId: u.userId,
                accessLevel: level as never,
                enabled: true,
              },
            },
          });
        }
        for (const t of pending.teams) {
          await shareItemTeam({
            variables: {
              input: {
                issueId: pending.issueId,
                teamId: t.teamId,
                accessLevel: level as never,
                enabled: true,
              },
            },
          });
        }
        // Only dismiss on success — closing on failure would make a
        // rejected grant look identical to a granted one, silently
        // leaving the mentioned user without access.
        trackGrant("issue", level, pending);
        close();
      } catch (error) {
        console.error("Failed to grant item access:", error);
        toastMutationError(error, "Couldn't grant access. Please try again.");
        setSubmitting(false);
      }
    },
    [pending, shareItem, shareItemTeam, close],
  );

  const grantWorkflowAccess = useCallback(
    async (level: ObjectAccessLevel) => {
      if (!pending || !workflowId) return;
      setSubmitting(true);
      try {
        for (const u of pending.users) {
          await shareWorkflow({
            variables: {
              input: {
                workflowId,
                userId: u.userId,
                accessLevel: level as never,
                enabled: true,
              },
            },
          });
        }
        for (const t of pending.teams) {
          await shareWorkflowTeam({
            variables: {
              input: {
                workflowId,
                teamId: t.teamId,
                accessLevel: level as never,
                enabled: true,
              },
            },
          });
        }
        // See grantItemAccess — dismiss only after every grant lands.
        trackGrant("workflow", level, pending);
        close();
      } catch (error) {
        console.error("Failed to grant workflow access:", error);
        toastMutationError(error, "Couldn't grant access. Please try again.");
        setSubmitting(false);
      }
    },
    [pending, workflowId, shareWorkflow, shareWorkflowTeam, close],
  );

  return {
    pending,
    setPending,
    submitting,
    close,
    lookupMissing,
    grantItemAccess,
    grantWorkflowAccess,
  };
}
